import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { QRCodeSVG } from 'qrcode.react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import { DownloadIcon, PersonIcon } from '../../../model/icons';
import Modal from '../../controls/Modal';
import LabeledInput from '../../controls/LabeledInput';
import { formatDt, participantName, qrCodeUrl } from './participants/participantsUtils';
import { CodeEventRow } from './participants/ParticipantDetailRows';

dayjs.extend(utc);

const CODE_TYPES = [
    { key: 'coin', name: 'Обычные коды' },
    { key: 'long', name: 'Обесценивающиеся' },
    { key: 'question', name: 'С вопросом' }
];

const formatDate = (d) => dayjs.utc(d).format('YYYY-MM-DD HH:mm');

function groupByDate(list) {
    return new Map(Object.entries(list.reduce((acc, item) => {
        (acc[item.createdDate] = acc[item.createdDate] || []).push(item);
        return acc;
    }, {})));
}

// Панель «Коды»: выбор типа, генератор, группы кодов по дате.
export default function CodesPanel({ neighbor, smenaId }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [type, setType] = useState('coin');
    const [param, setParam] = useState({ mask: '******', cost: '10', count: '1', qrBgColor: '#FFFFFF', qrCodeColor: '#000000' });
    const setP = (f, v) => setParam(prev => ({ ...prev, [f]: v }));

    // refetch вместо смены ключа: данные остаются на экране во время обновления,
    // список не размонтируется и QR не перегенерируются.
    const { data: codeList = [], refetch } = useApiQuery(
        ['neighbor', 'codes', type, smenaId],
        `/api/neighbor/admin/${type}-codes/list-by-neighbor?smenaId=${smenaId}`,
        { enabled: !!smenaId }
    );

    // Печать coin-кодов — готовый XLSX с бэка (4 колонки, A4, рамки), не постобрабатываем.
    const downloadPrintXlsx = async () => {
        show();
        try {
            const resp = await apiClient.get(`/api/neighbor/admin/coin-codes/download-print-xlsx?smenaId=${smenaId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'coin_codes.xlsx';
            link.click();
            window.URL.revokeObjectURL(url);
            enqueueSnackbar('Файл для печати скачан', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar(e.response?.status === 404 ? 'Игра для смены не найдена' : 'Ошибка при скачивании файла для печати', { variant: 'error' });
        } finally { hide(); }
    };

    const generate = async () => {
        show();
        const body = { smenaId, mask: param.mask, cost: parseInt(param.cost, 10), count: parseInt(param.count, 10) };
        if (type !== 'coin') { body.qrBgColor = param.qrBgColor; body.qrCodeColor = param.qrCodeColor; }
        try {
            await apiClient.post(`/api/neighbor/admin/${type}-codes/generate`, body);
            enqueueSnackbar('Коды созданы', { variant: 'success' });
            refetch();
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при генерации кодов', { variant: 'error' }); }
        finally { hide(); }
    };

    return (
        <div className="bt-panel">
            <section className="fr-card">
                <div className="sp-secthead"><h3>Тип кодов</h3></div>
                <div className="sp-chips">
                    {CODE_TYPES.map(t => (
                        <span key={t.key} className={`sp-chip${type === t.key ? ' on' : ''}`} role="button" tabIndex={0} onClick={() => setType(t.key)} onKeyDown={(e) => { if (e.key === 'Enter') setType(t.key); }}>{t.name}</span>
                    ))}
                </div>
            </section>

            <div className={type === 'coin' ? 'sp-genwrap' : 'sp-gen2'}>
                {type !== 'coin' && (
                    <section className="fr-card sp-qrparams">
                        <h3>Параметры QR кода</h3>
                        <div className="sp-colors">
                            <div className="sp-color"><input type="color" value={param.qrBgColor} onChange={(e) => setP('qrBgColor', e.target.value)} /><span>Цвет фона</span></div>
                            <div className="sp-color"><input type="color" value={param.qrCodeColor} onChange={(e) => setP('qrCodeColor', e.target.value)} /><span>Цвет QR</span></div>
                        </div>
                    </section>
                )}
                <section className="fr-card sp-gen">
                    <h3>Генератор кодов</h3>
                    <LabeledInput label="Маска" value={param.mask} onChange={(e) => setP('mask', e.target.value)} />
                    <div className="sp-genrow">
                        <LabeledInput label="Цена" type="number" value={param.cost} onChange={(e) => setP('cost', e.target.value)} />
                        <LabeledInput label="Кол-во" type="number" value={param.count} onChange={(e) => setP('count', e.target.value)} />
                    </div>
                    <button type="button" className="btn btn-primary" style={{ width: '100%' }} onClick={generate}>Сгенерировать</button>
                    <div className="sp-genhint">Маска — [******], где * — любое число и символ. Цена и кол-во — только неотрицательные целые числа.</div>
                </section>
            </div>

            {type === 'coin' && codeList.length > 0 && (
                <div className="sp-printrow">
                    <button type="button" className="btn btn-primary btn-sm" onClick={downloadPrintXlsx}>
                        <span className="ic"><DownloadIcon /></span>Скачать
                    </button>
                </div>
            )}

            <CodeGroups type={type} codeList={codeList} smenaId={smenaId} qrBaseUrl={neighbor.qrBaseUrl} onRefresh={refetch} />
        </div>
    );
}

CodesPanel.propTypes = { neighbor: PropTypes.object.isRequired, smenaId: PropTypes.number };
CodesPanel.defaultProps = { smenaId: null };

function CodeGroups({ type, codeList, smenaId, qrBaseUrl, onRefresh }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [qModal, setQModal] = useState(null);
    const [lModal, setLModal] = useState(null);
    const [answered, setAnswered] = useState(null); // id фактически выпавшего вопроса
    const groups = groupByDate(codeList);

    const delGroup = async (date) => {
        show();
        try {
            await apiClient.delete(`/api/neighbor/admin/${type}-codes/batch-delete`, { data: { smenaId, date: formatDate(date) } });
            enqueueSnackbar('Удалено', { variant: 'success' });
            onRefresh();
        } catch (e) { enqueueSnackbar('Ошибка при удалении кодов', { variant: 'error' }); }
        finally { hide(); }
    };

    const saveBlob = (data, name, date) => {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name}_${formatDate(date).replace(/[:\s]/g, '_')}`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    const dlGroup = async (date) => {
        show();
        try {
            const resp = await apiClient.post(`/api/neighbor/admin/${type}-codes/download-archive`, { smenaId, date: formatDate(date) }, { responseType: 'blob' });
            saveBlob(new Blob([resp.data], { type: 'application/zip' }), `${type}_codes`, `${date}.zip`);
            enqueueSnackbar('Скачивание началось', { variant: 'success' });
        } catch (e) { enqueueSnackbar('Ошибка при скачивании кодов', { variant: 'error' }); }
        finally { hide(); }
    };

    if (groups.size === 0) return <div className="bt-empty">Нет созданных кодов</div>;
    const sortedGroups = [...groups].sort(([a], [b]) => new Date(b) - new Date(a));

    return (
        <>
            {/* Стабильный порядок по значению кода: бэк меняет порядок после использования, коды «прыгали» */}
            {sortedGroups.map(([date, codes]) => [date, [...codes].sort((a, b) => String(a.value).localeCompare(String(b.value)))]).map(([date, codes]) => (
                <section className="sp-cg" key={date}>
                    <div className="sp-cg-head">
                        <div className="sp-cg-date">{formatDate(date)}</div>
                        <div className="sp-cg-acts">
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => delGroup(date)}>Удалить</button>
                            {type !== 'coin' && (
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => dlGroup(date)}><span className="ic"><DownloadIcon /></span>Скачать</button>
                            )}
                        </div>
                    </div>
                    {type === 'coin' ? (
                        <div className="sp-codechips">
                            {codes.map(c => (
                                <span key={c.id} className={`sp-codechip${c.used ? ' used' : ''}`}>
                                    <span className="val">{c.value}</span>
                                    {c.used && <span className="who">{c.usedBy ? participantName(c.usedBy) : 'игрок неизвестен'}</span>}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div className="sp-qrgrid">
                            {codes.map(c => (
                                <CodeCard
                                    key={c.id}
                                    code={c}
                                    type={type}
                                    qrBaseUrl={qrBaseUrl}
                                    onOpen={type === 'question' ? setQModal : setLModal}
                                />
                            ))}
                        </div>
                    )}
                </section>
            ))}
            {qModal && <QuestionCodeModal code={qModal} onClose={() => setQModal(null)} onSaved={onRefresh} onOpenAnswered={setAnswered} qrBaseUrl={qrBaseUrl} />}
            {lModal && <LongCodeModal code={lModal} onClose={() => setLModal(null)} />}
            {answered != null && <AnsweredQuestionView id={answered} qrBaseUrl={qrBaseUrl} onClose={() => setAnswered(null)} />}
        </>
    );
}

CodeGroups.propTypes = {
    type: PropTypes.string.isRequired,
    codeList: PropTypes.array.isRequired,
    smenaId: PropTypes.number,
    qrBaseUrl: PropTypes.string,
    onRefresh: PropTypes.func.isRequired
};
CodeGroups.defaultProps = { smenaId: null, qrBaseUrl: null };

// React.memo + стабильный onOpen: открытие/закрытие модалок не перерисовывает QR-сетку.
const CodeCard = React.memo(function CodeCard({ code, type, qrBaseUrl, onOpen }) {
    const path = type === 'long' ? 'longCode' : 'questionCode';
    const qrValue = qrCodeUrl(qrBaseUrl, path, code.value);
    const open = onOpen ? () => onOpen(code) : undefined;
    return (
        <div className={`sp-qrcard${open ? ' clickable' : ''}`} role={open ? 'button' : undefined} tabIndex={open ? 0 : undefined} onClick={open} onKeyDown={open ? (e) => { if (e.key === 'Enter') open(); } : undefined}>
            <div className="sp-qrimg"><QRCodeSVG value={qrValue} size={96} level="M" /></div>
            <div className="sp-qrcode">{code.value}</div>
            {type === 'long' && code.usedCount > 0 && <div className="sp-qrmeta">использований: {code.usedCount}</div>}
            {type === 'question' && code.question && <div className="sp-qrmeta q">вопрос задан</div>}
            {type === 'question' && code.usages?.length > 0 && <div className="sp-qrmeta">использований: {code.usages.length}</div>}
        </div>
    );
});

CodeCard.propTypes = {
    code: PropTypes.object.isRequired,
    type: PropTypes.string.isRequired,
    qrBaseUrl: PropTypes.string,
    onOpen: PropTypes.func
};
CodeCard.defaultProps = { qrBaseUrl: null, onOpen: undefined };

function QuestionCodeModal({ code, onClose, onSaved, onOpenAnswered, qrBaseUrl }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show, hide } = usePreloaderStore();
    const [q, setQ] = useState(code.question || '');
    const [a, setA] = useState(code.answer || '');
    // Использования этого кода → та же плитка, что на вкладке участника (usedBy показывает, кто сканировал).
    const usageEvents = [...(code.usages || [])]
        .sort((x, y) => new Date(y.usedAt) - new Date(x.usedAt))
        .map((u, i) => ({
            id: u.id ?? i,
            codeType: 'QUESTION',
            codeValue: code.value,
            usedBy: u.usedBy,
            reward: u.reward,
            answeredCorrect: u.answeredCorrect,
            answeredQuestionCodeId: u.answeredQuestionCodeId,
            playerAnswer: u.playerAnswer,
            activatedAt: u.usedAt
        }));

    const save = async () => {
        show();
        try {
            await apiClient.patch(`/api/neighbor/admin/question-codes/update?id=${code.id}`, { question: q, answer: a });
            enqueueSnackbar('Сохранено', { variant: 'success' });
            onSaved?.();
            onClose();
        } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Ошибка при сохранении', { variant: 'error' }); }
        finally { hide(); }
    };

    return (
        <Modal
            open
            kicker="Код с вопросом"
            title="Редактирование"
            size="xl"
            onClose={onClose}
            footer={(
                <>
                    <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
                    <button type="button" className="btn btn-primary" onClick={save}>Сохранить</button>
                </>
            )}
        >
            <div className="sp-qchip"><span className="l">Код</span>{code.value}</div>
            <div><label className="ctl-label">Вопрос</label><textarea className="textarea" value={q} onChange={(e) => setQ(e.target.value)} /></div>
            <div><label className="ctl-label">Ответ</label><textarea className="textarea" value={a} onChange={(e) => setA(e.target.value)} /></div>

            <div>
                <label className="ctl-label">Использования {usageEvents.length > 0 && `(${usageEvents.length})`}</label>
                {usageEvents.length > 0 ? (
                    <div className="sp-codegrid">
                        {usageEvents.map(ev => <CodeEventRow key={ev.id} event={ev} qrBaseUrl={qrBaseUrl} onOpenAnswered={onOpenAnswered} />)}
                    </div>
                ) : <div className="bt-empty">Пока никто не отсканировал</div>}
            </div>
        </Modal>
    );
}

QuestionCodeModal.propTypes = {
    code: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func,
    onOpenAnswered: PropTypes.func,
    qrBaseUrl: PropTypes.string
};

QuestionCodeModal.defaultProps = { onSaved: undefined, onOpenAnswered: undefined, qrBaseUrl: null };

// Просмотр (только чтение) фактически выпавшего вопроса. Грузит код по id (find-by-id по answeredQuestionCodeId):
// показывает QR, вопрос и эталонный ответ. Использования этого кода тут НЕ показываем (там история сканирований QR).
export function AnsweredQuestionView({ id, qrBaseUrl, onClose }) {
    const { data: code, isLoading } = useApiQuery(
        ['neighbor', 'question-code', id],
        `/api/neighbor/admin/question-codes/find-by-id?id=${id}`,
        { enabled: id != null, refetchOnMount: 'always' }
    );
    const qrValue = code?.value ? qrCodeUrl(qrBaseUrl, 'questionCode', code.value) : null;

    return (
        <Modal
            open
            kicker="Фактический вопрос"
            title={code?.value ? `Код ${code.value}` : 'Вопрос'}
            onClose={onClose}
            footer={<button type="button" className="btn btn-primary" onClick={onClose}>Закрыть</button>}
        >
            {!code ? (
                <div className="bt-empty">{isLoading ? 'Загрузка…' : 'Вопрос не найден'}</div>
            ) : (
                <>
                    {qrValue && <div className="sp-qrview"><QRCodeSVG value={qrValue} size={120} level="M" /></div>}
                    <div className="sp-qview"><label className="ctl-label">Вопрос</label><div className="sp-qtext">{code.question?.trim() || '— не заполнен —'}</div></div>
                    <div className="sp-qview"><label className="ctl-label">Эталонный ответ</label><div className="sp-qtext">{code.answer?.trim() || '— не заполнен —'}</div></div>
                </>
            )}
        </Modal>
    );
}

AnsweredQuestionView.propTypes = {
    id: PropTypes.number.isRequired,
    qrBaseUrl: PropTypes.string,
    onClose: PropTypes.func.isRequired
};

AnsweredQuestionView.defaultProps = { qrBaseUrl: null };

// Модалка обесценивающегося кода: только просмотр использований (редактировать нечего — нет полей вопроса/ответа).
function LongCodeModal({ code, onClose }) {
    const usages = [...(code.usages || [])].sort((a, b) => new Date(b.usedAt) - new Date(a.usedAt));

    return (
        <Modal
            open
            kicker="Обесценивающийся код"
            title={code.value}
            onClose={onClose}
            footer={<button type="button" className="btn btn-primary" onClick={onClose}>Закрыть</button>}
        >
            <div className="sp-qchip"><span className="l">Стоимость</span>{code.cost}</div>
            <div>
                <label className="ctl-label">Использования {usages.length > 0 && `(${usages.length})`}</label>
                {usages.length > 0 ? (
                    <div className="sp-part-list">
                        {usages.map((u, i) => <UsageRow key={i} usage={u} isQuestion={false} />)}
                    </div>
                ) : <div className="bt-empty">Пока никто не отсканировал</div>}
            </div>
        </Modal>
    );
}

LongCodeModal.propTypes = {
    code: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};

// Строка использования кода (.sp-erow): игрок + результат/начисление + время.
// Для question-кодов: результат ответа, ответ игрока и КОД фактически выпавшего вопроса (грузим по
// answeredQuestionCodeId → find-by-id); клик по коду → вопрос/эталонный ответ/QR.
function UsageRow({ usage, isQuestion, onOpenAnswered }) {
    const { usedBy, usedAt, reward, answeredCorrect, playerAnswer, answeredQuestionCodeId } = usage;

    const { data: answeredCode } = useApiQuery(
        ['neighbor', 'question-code', answeredQuestionCodeId],
        `/api/neighbor/admin/question-codes/find-by-id?id=${answeredQuestionCodeId}`,
        { enabled: isQuestion && answeredQuestionCodeId != null }
    );

    let badge = null;
    if (isQuestion) {
        if (answeredCorrect === true) badge = <span className="sp-badge done">Ответил верно{reward != null ? ` · +${reward}` : ''}</span>;
        else if (answeredCorrect === false) badge = <span className="sp-badge lose">Ответил неверно</span>;
        else badge = <span className="sp-badge wait">Не ответил / неизвестно</span>;
    } else if (reward != null) {
        badge = <span className="sp-badge done">+{reward}</span>;
    }

    const open = isQuestion && answeredQuestionCodeId != null && onOpenAnswered ? () => onOpenAnswered(answeredQuestionCodeId) : null;

    return (
        <div className="sp-erow">
            <div className="er-l">
                <div className="er-u"><PersonIcon />{usedBy ? participantName(usedBy) : 'Игрок неизвестен'}</div>
                {badge && <div className="er-badges" style={{ marginTop: 7 }}>{badge}</div>}
                {isQuestion && playerAnswer && (
                    <div className="sp-panswer"><span className="l">Ответ игрока</span><span className="v">{playerAnswer}</span></div>
                )}
                {isQuestion && answeredQuestionCodeId != null && (
                    <div className="er-q">
                        <span className="er-qlabel">отвечал на: </span>
                        {open ? (
                            <button type="button" className="er-qlink" onClick={open}>{answeredCode?.value || 'открыть вопрос'}</button>
                        ) : (answeredCode?.value || 'открыть вопрос')}
                    </div>
                )}
            </div>
            <div className="er-r">
                <div className="er-when">{formatDt(usedAt)}</div>
            </div>
        </div>
    );
}

UsageRow.propTypes = {
    usage: PropTypes.shape({
        usedBy: PropTypes.object,
        usedAt: PropTypes.string,
        reward: PropTypes.number,
        answeredCorrect: PropTypes.bool,
        answeredQuestionCodeId: PropTypes.number,
        playerAnswer: PropTypes.string
    }).isRequired,
    isQuestion: PropTypes.bool.isRequired,
    onOpenAnswered: PropTypes.func
};

UsageRow.defaultProps = { onOpenAnswered: undefined };
